import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { LeadsService } from "../../../services/user/leads.services";
import { CustomerService } from "../../../services/user/customer.services";
import { ActivityService } from "../../../services/user/activity.services";
import { QuoteService } from "../../../services/user/quotes.services";
import { InvoiceService } from "../../../services/user/invoices.services";
import { getButtonStyles } from "../../../utility";

const SummaryComponent = () => {
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [customerData, setCustomerData] = useState([]);
    const [leadData, setLeadData] = useState([]);
    const [activityData, setActivityData] = useState([]);
    const [quoteData, setQuoteData] = useState([]);
    const [invoiceData, setInvoiceData] = useState([]);
    const [, setLoading] = useState(false);
    // Status filter & title colors
    // const [selectedLeadStatus, setSelectedLeadStatus] = useState(null);
    // const [leadTitleColor, setLeadTitleColor] = useState("#6c757d");
    const [, setSelectedActivityStatus] = useState(null);
    const [, setActivityTitleColor] = useState("#6c757d");

    // Fetch data
    useEffect(() => {
        const fetchAll = async () => {
            try {
                setLoading(true);
                setCustomerData(await CustomerService.getAllCustomers(selectedStore));
                setLeadData(await LeadsService.getAllLeads(selectedStore));
                setActivityData(await ActivityService.getAllActivities(selectedStore));
                setQuoteData(await QuoteService.getAllQuotes(selectedStore));
                setInvoiceData(await InvoiceService.getAllInvoices(selectedStore));
            } catch (err) {
                console.error("Error fetching summary data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAll();
    }, [selectedStore]);

    // Calculate customer statistics
    const totalCustomers = customerData.length;
    const activeCustomers = customerData.filter(customer => customer.isActive).length;
    const inactiveCustomers = totalCustomers - activeCustomers;

    // leads data by status
    const leadsByStatus = leadData.reduce((acc, lead) => {
        const { status } = lead;
        if (!acc[status?.statusName]) {
            acc[status?.statusName] = { count: 0, value: 0 };
        }
        acc[status?.statusName].count += 1;
        acc[status?.statusName].value += lead.leadValue;
        return acc;
    }, {});

    // total lead value
    // const totalLeadValue = leadData.reduce((acc, lead) => acc + lead.leadValue, 0);

    // Activity data by status
    const activitiesByStatus = activityData.reduce((acc, activity) => {
        const { status } = activity;
        if (!acc[status?.statusName]) {
            acc[status?.statusName] = { count: 0 };
        }
        acc[status?.statusName].count += 1;
        return acc;
    }, {});

    // Quotes by Status
    const quotesByStatus = quoteData.reduce((acc, q) => {
        const status = q.status?.statusName || "Unknown";
        if (!acc[status]) acc[status] = { count: 0, value: 0 };
        acc[status].count++;
        acc[status].value += q.total;
        return acc;
    }, {});
    const totalQuotes = quoteData.length;
    const approvedQuotes = quoteData.filter(q => q.user?.approvalForQuote).length;
    const unapprovedQuotes = totalQuotes - approvedQuotes;

    // Invoices by Status
    const invoiceByStatus = invoiceData.reduce((acc, inv) => {
        const status = inv.status?.statusName || "Unknown";
        if (!acc[status]) acc[status] = { count: 0, value: 0 };
        acc[status].count++;
        acc[status].value += inv.total || inv.invoiceValue || 0;
        return acc;
    }, {});
    const totalInvoices = invoiceData.length;
    const approvedInvoices = invoiceData.filter(i => i.user?.approvalForInvoice).length;
    const unapprovedInvoices = totalInvoices - approvedInvoices;


    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                {/* Lead Summary */}
                <h5 style={{ color: "#6c757d" }} className="mt-4 d-flex">Lead Summary</h5>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3 mt-1" >
                    {Object.keys(leadsByStatus).map((statusName) => {
                        const { count, value } = leadsByStatus[statusName];
                        const percentage = ((count / leadData.length) * 100).toFixed(2);
                        const { backgroundColor } = getButtonStyles({
                            colorId: leadData.find(lead => lead.status.statusName === statusName)?.status.colorId
                        });
                        return (
                            <div key={statusName} className="col-12 col-sm-6 col-lg-2"
                            // onClick={() => handleLeadStatusClick(statusName)}
                            >
                                <div className="card radius-10"
                                    style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body">
                                        <h5 className="mb-0 d-flex" style={{ color: backgroundColor }}>{statusName}</h5>
                                        <h4 className="my-1 text-dark d-flex">₹ {value.toFixed(2)}</h4>
                                        <div className="d-flex justify-content-between">
                                            <div className="mb-0 text-info">{count} Leads</div>
                                            <div className="mb-0 text-info">{percentage}%</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Activity Summary */}
                <h5 style={{ color: "#6c757d" }} className="mt-4 d-flex">Activity Summary</h5>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3 mt-1">
                    {Object.keys(activitiesByStatus).map((statusName) => {
                        const { count } = activitiesByStatus[statusName];
                        const percentage = ((count / activityData.length) * 100).toFixed(2);

                        const { backgroundColor } = getButtonStyles({
                            colorId: activityData.find(activity => activity.status.statusName === statusName)?.status.colorId
                        });

                        return (
                            <div key={statusName}
                                className="col-12 col-sm-6 col-lg-2"
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                    setSelectedActivityStatus(statusName);
                                    setActivityTitleColor(backgroundColor);
                                }}
                            >
                                <div className="card radius-10"
                                    style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body text-center">
                                        <h5 className="mb-1 d-flex" style={{ color: backgroundColor }}>
                                            {statusName}
                                        </h5>
                                        <h5 className="text-dark d-flex">{count}</h5>
                                        <p className="mb-0 text-info d-flex">{percentage}%</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Customer Summary */}
                <h5 style={{ color: "#6c757d" }} className="mt-4 d-flex">Customer Summary</h5>
                <div className="row mt-4">
                    {/* Total Customers */}
                    <div className="col-md-4">
                        <div className="card radius-10 border-start border-0 border-4 border-warning">
                            <div className="card-body">
                                <h5 className="mb-0 text-warning d-flex">Total Customers</h5>
                                <h4 className="my-1 text-dark d-flex">{totalCustomers}</h4>
                                <div className="d-flex justify-content-between align-item-center text-primary">
                                    <p className="mb-0 font-13">{((totalCustomers / totalCustomers) * 100).toFixed(2)}% Customer</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Active Customers */}
                    <div className="col-md-4">
                        <div className={`card radius-10 border-start border-0 border-4 border-success ${(activeCustomers)}`}>
                            <div className="card-body">
                                <h5 className="mb-0 text-success d-flex">Active Customers</h5>
                                <h4 className="my-1 text-dark d-flex">{activeCustomers}</h4>
                                <div className="d-flex justify-content-between align-item-center text-primary">
                                    <p className="mb-0 font-13">{((activeCustomers / totalCustomers) * 100).toFixed(2)}% Active Customer</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Inactive Customers */}
                    <div className="col-md-4">
                        <div className={`card radius-10 border-start border-0 border-4 border-danger ${(inactiveCustomers)}`}>
                            <div className="card-body">
                                <h5 className="mb-0 text-danger d-flex">Inactive Customers</h5>
                                <h4 className="my-1 text-dark d-flex">{inactiveCustomers}</h4>
                                <div className="d-flex justify-content-between align-item-center text-primary">
                                    <p className="mb-0 font-13">{((inactiveCustomers / totalCustomers) * 100).toFixed(2)}% Inactive Customers</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Quotes Summary */}
                <h5 style={{ color: "#6c757d" }} className="mt-4 d-flex">Quotes Summary</h5>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 g-4 mt-1">
                    {Object.keys(quotesByStatus).map(statusName => {
                        const { count, value } = quotesByStatus[statusName];
                        const percentage = ((count / totalQuotes) * 100).toFixed(2);
                        const { backgroundColor } = getButtonStyles({
                            colorId: quoteData.find(q => q.status?.statusName === statusName)?.status?.colorId
                        });
                        return (
                            <div key={statusName} className="col">
                                <div className="card radius-10" style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body">
                                        <h5 className="d-flex" style={{ color: backgroundColor }}>{statusName}</h5>
                                        <h5 className="d-flex">₹ {value.toFixed(2)}</h5>
                                        <div className="d-flex justify-content-between">
                                            <p className="mb-0 font-13 d-flex text-primary">{count} Quotes</p>
                                            <p className="mb-0 font-13 text-primary">{percentage}%</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {/* Approved / Unapproved Card */}
                    <div className="col">
                        <div className="card radius-10 border-start border-0 border-4 border-primary">
                            <div className="card-body">
                                <h5 className="d-flex text-primary">Approved / Unapproved</h5>
                                <h5 className="">{approvedQuotes} / {unapprovedQuotes}</h5>
                                <div className="d-flex justify-content-between">
                                    <p className="mb-0 font-13 text-primary">{approvedQuotes} Approved</p>
                                    <p className="mb-0 font-13 text-primary">{unapprovedQuotes} Unapproved</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Invoices Summary */}
                <h5 style={{ color: "#6c757d" }} className="mt-4 d-flex">Invoices Summary</h5>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-6 g-4 mt-1">
                    {Object.keys(invoiceByStatus).map(status => {
                        const { count, value } = invoiceByStatus[status];
                        const perc = ((count / totalInvoices) * 100).toFixed(2);
                        const { backgroundColor } = getButtonStyles({
                            colorId: invoiceData.find(i => i.status?.statusName === status)?.status.colorId
                        });
                        return (
                            <div key={status} className="col-12 col-sm-6 col-lg-2">
                                <div className="card radius-10" style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body">
                                        <h5 className="d-flex" style={{ color: backgroundColor }}>{status}</h5>
                                        <h5 className="d-flex">₹ {value.toFixed(2)}</h5>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-primary">{count} Invoices</span>
                                            <span className="text-primary">{perc}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {/* Approved/Unapproved Card */}
                    <div className="col">
                        <div className="card radius-10 border-start border-0 border-4 border-primary">
                            <div className="card-body">
                                <h5 className="d-flex text-primary">Approved / Unapproved</h5>
                                <h5>{approvedInvoices} / {unapprovedInvoices}</h5>
                                <div className="d-flex justify-content-between">
                                    <span className="mb-0 font-13 text-primary">{approvedInvoices} Approved</span>
                                    <span className="mb-0 font-13 text-primary">{unapprovedInvoices} Unapproved</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tenders Summary */}
                <h5 style={{ color: "#6c757d" }} className="mt-4 d-flex">Tender Summary</h5>
                <div className="row mt-4">
                    {/* Sd Mature Tenders */}
                    <div className="col-md-4">
                        <a href="/tenders" target="_blank" className="text-decoration-none">
                            <div className="card radius-10 border-start border-0 border-4 border-warning p-3">
                                <div className="card-body d-flex justify-content-between">
                                    <div>
                                        <h5 className="text-primary d-flex">Sd Mature Tenders</h5>
                                        <p className="text-dark small mb-0">Total tenders going to mature in 30 days</p>
                                    </div>
                                    <h1 className="text-dark mb-0">0</h1>
                                </div>
                            </div>
                        </a>
                    </div>
                    {/* PBG Mature Tenders */}
                    <div className="col-md-4">
                        <a href="/tenders" target="_blank" className="text-decoration-none">
                            <div className="card radius-10 border-start border-0 border-4 border-success p-3">
                                <div className="card-body d-flex justify-content-between">
                                    <div>
                                        <h5 className="text-success d-flex">PBG Mature Tenders</h5>
                                        <p className="text-dark small mb-0">Total tenders going to mature in 30 days</p>
                                    </div>
                                    <h1 className="text-dark mb-0">0</h1>
                                </div>
                            </div>
                        </a>
                    </div>
                    {/* Received Tenders */}
                    <div className="col-md-4">
                        <a href="/tenders" target="_blank" className="text-decoration-none">
                            <div className="card radius-10 border-start border-0 border-4 border-danger p-3">
                                <div className="card-body  d-flex justify-content-between">
                                    <div>
                                        <h5 className="text-danger d-flex">Received Tenders</h5>
                                        <p className="text-dark small mb-0">Total tenders going to received in 30 days</p>
                                    </div>
                                    <h1 className="text-dark mb-0">0</h1>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummaryComponent;